import React, { useEffect, useState, useCallback } from "react";
import * as R from "ramda";
import StyleWrapper from "components/Global/Modal/Layout/StyleWrapper";
import {
  ButtonOrange,
  ButtonOutline
} from "components/Global/Modal/Layout/Buttons";
import { noop } from "utils/General";
import { Cards, Div, SmallToggle, Text1, Text2 } from "components/Base";
import Checkbox from "material-ui/Checkbox";
import css from "./styles.scss";
import SlimPaginationBar from "./Pagination";
import Tooltip from "components/Global/Tooltip";

const SelectedSpeaker = ({
  name = "",
  email = "",
  toggleSpeaker = noop,
  id = null,
  isSpeakerPublic = true
}) => {
  return (
    <Div
      display="row.space-between.center"
      bg="white"
      bc="#E7E6EA"
      b={1}
      p={2}
      mb={2}
      style={{ borderRadius: "4px" }}
    >
      <Div
        mr={1}
        display="row.flex-start.center"
        style={{ wordBreak: "break-word", gap: 8 }}
      >
        <i
          style={{ color: "#868686", fontSize: "16px", cursor: "pointer" }}
          className="material-icons"
        >
          drag_indicator
        </i>
        {!isSpeakerPublic && (
          <span className="material-icons-outlined" style={{ fontSize: 14 }}>
            visibility_off
          </span>
        )}
        <Text2 bold color="#494949">
          {!name ? email : `${name}`}
        </Text2>
      </Div>
      <i
        style={{ color: "#868686", fontSize: "16px", cursor: "pointer" }}
        className="material-icons"
        onClick={() => toggleSpeaker(id)}
      >
        close
      </i>
    </Div>
  );
};

const SpeakerOption = ({
  isSelected,
  speaker,
  toggleSpeaker,
  toggleSpeakerIsPublic,
  addedSpeakers
}) => {
  const { id, name, email } = speaker || {};

  const isSpeakerPublic = R.compose(
    R.propOr(true, "isSpeakerPublic"),
    R.find(R.propEq("id", id))
  )(addedSpeakers);
  return (
    <Div
      width={1}
      mb={2}
      px={3}
      py={2.5}
      display="row.space-between.center"
      style={{ borderRadius: "4px", border: "1px solid #E7E6EA" }}
    >
      <Div display="row.space-between.center" width={0.8}>
        <Checkbox
          checked={isSelected}
          onClick={() => toggleSpeaker(id)}
          label={
            <Text2>
              {name} ({email})
            </Text2>
          }
        />
      </Div>

      <Tooltip
        tooltip={isSpeakerPublic ? "Public" : "Not Public"}
        style={{
          cursor: "pointer",
          zIndex: 9999999,
          position: "relative",
          width: "100%",
          height: "100%"
        }}
      >
        <span
          style={{ cursor: "pointer" }}
          onClick={() => toggleSpeakerIsPublic(id)}
        >
          <SmallToggle active={isSpeakerPublic} bg="#37B24D" />
        </span>
      </Tooltip>
    </Div>
  );
};

const SelectedSpeakersCards = new Cards(SelectedSpeaker, "FIELD");

const initialPagination = {
  pageSize: 10,
  pages: 0,
  page: 0,
  total: 0
};

const BulkEditSpeakers = ({
  hideModal = noop,
  speakersList = [],
  selectedSpeakers = [],
  onSave = noop,
  onCancel = noop
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const [addedSpeakers, setAddedSpeakers] = useState(selectedSpeakers);

  const [filteredSpeakers, setFilteredSpeakers] = useState(speakersList);
  const [displayedSpeakers, setDisplayedSpeakers] = useState(speakersList);
  const [pagination, setPagination] = useState(initialPagination);

  useEffect(() => {
    const upperSearchTerm = searchTerm.toUpperCase();
    setFilteredSpeakers(
      speakersList.filter(
        speaker =>
          speaker.name?.toUpperCase().includes(upperSearchTerm) ||
          speaker.email?.toUpperCase().includes(upperSearchTerm)
      )
    );
  }, [searchTerm, speakersList]);

  useEffect(() => {
    setPagination({
      pageSize: 10,
      pages:
        filteredSpeakers.length / 10 < 0
          ? 1
          : Math.ceil(filteredSpeakers.length / 10),
      page: 0,
      total: filteredSpeakers.length
    });
  }, [filteredSpeakers]);

  useEffect(() => {
    const startIndex = pagination.page * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    setDisplayedSpeakers(filteredSpeakers.slice(startIndex, endIndex));
  }, [filteredSpeakers, pagination.page, pagination.pageSize]);

  const isSpeakerAdded = useCallback(
    speakerId => addedSpeakers.some(speaker => speaker.id === speakerId),
    [addedSpeakers]
  );

  const toggleSpeaker = useCallback(
    speakerId => {
      if (isSpeakerAdded(speakerId)) {
        setAddedSpeakers(
          addedSpeakers.filter(speaker => speaker.id !== speakerId)
        );
      } else {
        setAddedSpeakers([
          ...addedSpeakers,
          speakersList.find(speaker => speaker.id === speakerId)
        ]);
      }
    },
    [addedSpeakers, isSpeakerAdded, speakersList]
  );

  const closeModal = useCallback(() => {
    hideModal();
    onCancel();
  }, [hideModal, onCancel]);

  const saveSpeakers = useCallback(() => {
    const parsedSpeakers = addedSpeakers.map(speaker => ({
      ...speaker,
      value: speaker.id,
      label: speaker.name
        ? `${speaker.name} ${speaker.id}`
        : `${speaker.email} ${speaker.id}` || "",
      isSpeakerPublic: speaker?.isSpeakerPublic ?? true
    }));
    onSave(parsedSpeakers);
    hideModal();
  }, [addedSpeakers, hideModal, onSave]);

  const none = useCallback(() => {
    const displayedIds = displayedSpeakers.map(speaker => speaker.id);
    setAddedSpeakers([
      ...addedSpeakers.filter(speaker => !displayedIds.includes(speaker.id))
    ]);
  }, [addedSpeakers, displayedSpeakers]);

  const all = useCallback(() => {
    setAddedSpeakers(prevAddedSpeakers => {
      const newSpeakers = displayedSpeakers.filter(
        speaker => !prevAddedSpeakers.some(added => added.id === speaker.id)
      );
      return [...prevAddedSpeakers, ...newSpeakers];
    });
  }, [displayedSpeakers]);

  const clear = useCallback(() => setAddedSpeakers([]), []);

  const handlePageChange = useCallback(newPage => {
    setPagination(prevPagination => ({
      ...prevPagination,
      page: newPage
    }));
  }, []);

  const toggleSpeakerIsPublic = useCallback(speakerId => {
    setAddedSpeakers(prevAddedSpeakers =>
      prevAddedSpeakers.map(speaker =>
        speaker.id === speakerId
          ? { ...speaker, isSpeakerPublic: !speaker.isSpeakerPublic }
          : speaker
      )
    );
  }, []);

  return (
    <StyleWrapper
      background={"red"}
      width={1000}
      heading="Edit Session Speakers"
      hideModal={closeModal}
      containerStyles={{ margin: 0, zIndex: 101 }}
      bodyStyles={{ padding: 0 }}
    >
      <Div
        display="row.space-between.center"
        style={{ height: "70dvh", overflow: "auto" }}
      >
        <Div
          style={{ width: "calc(100% - 304px)" }}
          height={1}
          display="column.flex-start.stretch"
          pl={8}
          py={5}
          pr={4}
        >
          <Div width={1} mb={4}>
            <Div className={css.searchBar}>
              <input
                placeholder="Search by name"
                type="text"
                onChange={({ target: { value } }) => setSearchTerm(value)}
                value={searchTerm}
              />
              <i className="material-icons">search</i>
            </Div>
          </Div>
          <Div display="row.space-between.center" mb={2}>
            <Div display="row.space-between.center">
              <Text2 color="primary7" onClick={all}>
                Select All
              </Text2>
              <Text2 ml={4} color="primary7" onClick={none}>
                None
              </Text2>
            </Div>
            <Text1 color="#868686" uppercase bold mr={2.8}>
              Public
            </Text1>
          </Div>
          <Div with={1} height={0.83} display="column.space-between.center">
            <Div
              display="column.flex-start.stretch"
              style={{ overflowY: "auto" }}
              height={0.9}
              width={1}
            >
              {displayedSpeakers.map(speaker => (
                <SpeakerOption
                  isSelected={isSpeakerAdded(speaker.id)}
                  toggleSpeaker={toggleSpeaker}
                  key={`speakerOption-${speaker.id}`}
                  speaker={speaker}
                  addedSpeakers={addedSpeakers}
                  toggleSpeakerIsPublic={toggleSpeakerIsPublic}
                />
              ))}
            </Div>

            <Div width={1} height={0.1} display="row.space-between.flex-end">
              <SlimPaginationBar
                pagination={pagination}
                onPageChange={handlePageChange}
              />
            </Div>
          </Div>
        </Div>
        <Div
          style={{
            width: "304px",
            height: "100%",
            borderLeft: "1px solid #E7E6EA",
            gap: "12px"
          }}
          px={3}
          py={5}
          bg="#F8F8F8"
        >
          <Div display="row.space-between.center" mb={2}>
            <Text2 color="#343434" bold>
              Selected speakers ({addedSpeakers.length})
            </Text2>
            {addedSpeakers.length > 0 && (
              <Text2 color="primary9" onClick={clear}>
                Remove All
              </Text2>
            )}
          </Div>
          <Div
            display="column.flex-start.stretch"
            style={{ height: "95%", overflowY: "auto" }}
          >
            <SelectedSpeakersCards
              cards={addedSpeakers}
              toggleSpeaker={toggleSpeaker}
              onReorder={orderedFields => setAddedSpeakers([...orderedFields])}
            />
          </Div>
        </Div>
      </Div>
      <Div
        px={8}
        py={4}
        display="row.flex-start.center"
        style={{ borderTop: "1px solid #E7E6EA", gap: "8px" }}
      >
        <ButtonOutline
          style={{ color: "#494949" }}
          onClick={closeModal}
          title={"Cancel"}
        />
        <ButtonOrange onClick={saveSpeakers} title="Save" />
      </Div>
    </StyleWrapper>
  );
};

export default BulkEditSpeakers;
